import React from 'react';
import { X, Check, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ServiceDetailsModal = ({ service, isOpen, onClose, onBookNow }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  if (!isOpen || !service) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 
            className="text-2xl font-bold text-white"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {currentLang === 'ru' ? service.name_ru : service.name_de}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Service Image */}
          {service.image && (
            <div className="w-full h-64 bg-gray-800 rounded-lg overflow-hidden">
              <img 
                src={service.image} 
                alt={currentLang === 'ru' ? service.name_ru : service.name_de}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">
              {t('services.details.description')}
            </h3>
            <p className="text-gray-300 leading-relaxed">
              {currentLang === 'ru' ? service.description_ru : service.description_de}
            </p>
          </div>

          {/* Features */}
          {service.features_de && service.features_de.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">
                {t('services.details.included')}
              </h3>
              <ul className="space-y-2">
                {(currentLang === 'ru' ? service.features_ru : service.features_de).map((feature, index) => (
                  <li key={index} className="flex items-center space-x-3 text-gray-300">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Base Price */}
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-2">
              {t('services.details.startingPrice')}
            </h3>
            <p className="text-2xl font-bold text-white">
              {service.price}
            </p>
          </div>

          {/* Tariffs */}
          {service.tariffs && service.tariffs.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">
                {t('services.details.availablePackages')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {service.tariffs.map((tariff) => (
                  <div 
                    key={tariff.id} 
                    className={`p-4 border rounded-lg ${
                      tariff.popular 
                        ? 'border-yellow-500 bg-yellow-500/10' 
                        : 'border-gray-600 bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-white">
                        {currentLang === 'ru' ? tariff.name_ru : tariff.name_de}
                      </h4>
                      {tariff.popular && (
                        <Star className="h-4 w-4 text-yellow-500 fill-current" />
                      )}
                    </div>
                    <p className="text-lg font-bold text-white mb-3">
                      {tariff.price}
                    </p>
                    {tariff.features_de && tariff.features_de.length > 0 && (
                      <ul className="space-y-1 text-sm text-gray-300">
                        {(currentLang === 'ru' ? tariff.features_ru || [] : tariff.features_de || []).map((feature, index) => (
                          <li key={index} className="flex items-center space-x-2">
                            <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {tariff.conditions_de && (
                      <p className="text-xs text-gray-400 mt-2">
                        {currentLang === 'ru' ? tariff.conditions_ru : tariff.conditions_de}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-700">
            <button
              onClick={onBookNow}
              className="flex-1 px-6 py-3 bg-white text-black font-semibold rounded-md hover:bg-gray-200 transition-colors duration-300"
            >
              {t('services.bookNow')}
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-600 text-white rounded-md hover:bg-gray-800 transition-colors duration-300"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailsModal;